import { z } from 'zod';
/**
 * Cross-domain workflow automation.
 *
 * Automations are deliberately narrow: a trigger condition, a SINGLE
 * allowlisted low-risk action, policy limits and a global kill switch.
 * Every run supports dry-run, is idempotency-keyed, rate-limited per rule,
 * and ends in an explicit terminal state. High-risk actions are not
 * expressible here at all — the action allowlist below is the policy, and
 * anything that suspends, bills, changes entitlements or touches a customer
 * system is absent from it by design. Actions that need a human decision
 * route through the Stage 2 approval engine instead of bypassing it.
 */
export const AUTOMATION_TRIGGERS = [
    'case.sla_breached',
    'health.band_dropped',
    'evidence.expiring',
    'invoice.overdue',
    'installation.offline',
];
export const AutomationTriggerSchema = z.enum(AUTOMATION_TRIGGERS);
/**
 * The complete set of actions an automation may take: notify a role, or open
 * a workspace task. Nothing punitive, financial or customer-facing.
 *
 * `raise_approval_request` was removed after review. Automation cannot
 * construct a valid approval: every approval action type carries a reviewed
 * approver-role policy AND a payload schema that `execute()` re-validates,
 * and a rule has no basis for inventing either. The version that shipped in
 * Stage 5 wrote `action_type: 'automation.review'`, which is not in
 * APPROVAL_ACTION_TYPES — the row could never be submitted or decided, and it
 * broke serialisation of the whole approval queue for every operator.
 *
 * The intent it served is fully covered by `notify_role`: a human is told,
 * and raises the real approval through the normal flow with a payload the
 * engine can actually execute. An approval path that cannot execute is worse
 * than none — it looks like governance while doing nothing.
 */
export const AUTOMATION_ACTIONS = ['notify_role', 'create_task'];
export const AutomationActionSchema = z.enum(AUTOMATION_ACTIONS);
export const RUN_STATUSES = ['dry_run', 'executed', 'suppressed', 'failed'];
export const RunStatusSchema = z.enum(RUN_STATUSES);
export const AutomationRuleSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    trigger: AutomationTriggerSchema,
    action: AutomationActionSchema,
    /** Action parameters, validated per action at execution time. */
    actionParams: z.record(z.string(), z.unknown()),
    enabled: z.boolean(),
    /** Max executions per hour; further matches are suppressed and recorded. */
    maxRunsPerHour: z.number().int().min(1).max(60),
    createdByDisplayName: z.string().nullable(),
    version: z.number().int(),
    updatedAt: z.iso.datetime(),
});
export const AutomationRuleInputSchema = z
    .object({
    name: z.string().min(5).max(120),
    trigger: AutomationTriggerSchema,
    action: AutomationActionSchema,
    actionParams: z.record(z.string(), z.unknown()).default({}),
    maxRunsPerHour: z.number().int().min(1).max(60).default(10),
})
    .strict();
export const AutomationRunSchema = z.object({
    id: z.uuid(),
    ruleId: z.uuid(),
    ruleName: z.string().optional(),
    status: RunStatusSchema,
    /** Trigger dedup key — one run per (rule, subject) occurrence. */
    idempotencyKey: z.string(),
    subjectType: z.string(),
    subjectId: z.string(),
    detail: z.string().nullable(),
    ranAt: z.iso.datetime(),
});
/**
 * Global kill switch. When engaged, the executor performs NO actions — every
 * match is recorded as suppressed with the reason — and disengaging requires
 * the actor and reason to be recorded. There is exactly one switch: partial
 * kill states are not expressible.
 */
export const KillSwitchSchema = z.object({
    engaged: z.boolean(),
    reason: z.string().nullable(),
    changedByDisplayName: z.string().nullable(),
    changedAt: z.iso.datetime().nullable(),
});
export const KillSwitchInputSchema = z
    .object({
    engaged: z.boolean(),
    reason: z.string().min(10).max(500),
})
    .strict();
//# sourceMappingURL=automation.js.map